import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies -- ky is a runtime dependency in apps/web (monorepo workspace)
import ky from 'ky';
import * as React from 'react';
import { getEdgeUrl, safeLocalStorage } from '../helpers';

// Define types to match the '/user' edge function response
interface UserData {
  contact: Record<string, unknown> | null;
}

interface UserContextValue {
  userData: UserData | null;
  isLoading: boolean;
  error: Error | null;
  refreshUserData: () => void;
  updateUserData: (data: Partial<UserData>) => void;
}

// Cache configuration
const CACHE_KEY = 'ch-ud';
const CACHE_EXPIRY_HOURS = 24; // Cache for 24 hours
const NEGATIVE_CACHE_KEY = 'ch-ud-neg';
const NEGATIVE_CACHE_EXPIRY_HOURS = 1; // Cache null contact for 1 hour to avoid refetch loops

// Create context
const UserContext = createContext<UserContextValue | undefined>(undefined);

// Provider component
export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUserData = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      // Check cache first
      const cachedData = safeLocalStorage.get<UserData>(CACHE_KEY, CACHE_EXPIRY_HOURS);
      if (cachedData && cachedData.contact) {
        setUserData(cachedData);
        setIsLoading(false);
        return;
      }

      // Check negative cache (recently known as null contact)
      const negativeCached = safeLocalStorage.get<boolean>(NEGATIVE_CACHE_KEY, NEGATIVE_CACHE_EXPIRY_HOURS);
      if (negativeCached) {
        setUserData({ contact: null });
        setIsLoading(false);
        return;
      }

      // Fetch fresh data
      const userUrl = getEdgeUrl('user');
      const data = await ky.get(userUrl).json<UserData>();
      setUserData(data);
      if (data?.contact) {
        safeLocalStorage.set(CACHE_KEY, data, CACHE_EXPIRY_HOURS); // Cache only if we have a contact
        safeLocalStorage.remove(NEGATIVE_CACHE_KEY);
      } else {
        safeLocalStorage.remove(CACHE_KEY);
        safeLocalStorage.set(NEGATIVE_CACHE_KEY, true, NEGATIVE_CACHE_EXPIRY_HOURS);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch user data'));
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUserData = (): void => {
    // Clear cache and refetch
    safeLocalStorage.remove(CACHE_KEY);
    safeLocalStorage.remove(NEGATIVE_CACHE_KEY);
    void fetchUserData();
  };

  const updateUserData = (partialData: Partial<UserData>): void => {
    if (!userData) return;
    
    const updatedData = { ...userData, ...partialData };
    setUserData(updatedData);
    safeLocalStorage.set(CACHE_KEY, updatedData, CACHE_EXPIRY_HOURS);
  };

  useEffect(() => {
    // Only run on client side
    if (typeof window !== 'undefined') {
      void fetchUserData();
    }
  }, []);

  const value: UserContextValue = {
    userData,
    isLoading,
    error,
    refreshUserData,
    updateUserData
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom hook to use the user context
export const useUserContext = (): UserContextValue => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};

// Export types for external use
export type { UserData, UserContextValue };
